/**
 * Moves Table
 * Displays asset movement reports with date range filtering, type/subtype filters,
 * pagination, and CSV export. Used in project page Moves tab.
 */

import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import {
  FileDownload as ExportIcon,
  PlayArrow as RunIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';

import PageBody from './PageBody';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchMovementReports } from '../../store/slices/reportSlice';
import { colorPalette } from '../../theme';
import { formatDateTime } from '../../utils/helpers';

const MovesTable = ({ projectId, customerId }) => {
  const dispatch = useAppDispatch();
  const { movementReports, loading } = useAppSelector((state) => state.report);
  const { assetTypes: assetTypeList, assetSubTypes: assetSubtypeList } = useAppSelector((state) => state.asset);

  // Default date range: 3 months back to now
  const defaultStart = new Date();
  defaultStart.setMonth(defaultStart.getMonth() - 3);
  
  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(new Date());
  const [typeFilter, setTypeFilter] = useState('');
  const [subtypeFilter, setSubtypeFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(8);

  const handleRunReport = useCallback(() => {
    dispatch(fetchMovementReports({ 
      customerId, 
      startDate: startDate?.toISOString().split('T')[0], 
      endDate: endDate?.toISOString().split('T')[0] 
    }));
  }, [dispatch, customerId, startDate, endDate]);

  // Auto-run on mount
  useEffect(() => {
    handleRunReport();
  }, [handleRunReport]);

  const filteredReports = useMemo(() => {
    let reports = movementReports || [];
    if (typeFilter) {
      reports = reports.filter((r) =>
        r.assetType?.name === typeFilter || r.type === typeFilter
      );
    }
    if (subtypeFilter) {
      reports = reports.filter((r) =>
        r.assetSubType?.name === subtypeFilter || r.subtype === subtypeFilter
      );
    }
    return reports;
  }, [movementReports, typeFilter, subtypeFilter]);

  const paginatedReports = filteredReports.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const uniqueTypes = useMemo(() => {
    const types = new Set();
    (movementReports || []).forEach((r) => {
      const name = r.assetType?.name || r.type;
      if (name) types.add(name);
    });
    return Array.from(types).sort();
  }, [movementReports]);

  const uniqueSubtypes = useMemo(() => {
    const subtypes = new Set();
    (movementReports || []).forEach((r) => {
      const name = r.assetSubType?.name || r.subtype;
      if (name) subtypes.add(name);
    });
    return Array.from(subtypes).sort();
  }, [movementReports]);

  const handleExport = () => {
    if (filteredReports.length === 0) return;
    const headers = [
      'Start Date', 'Start Time', 'End Date', 'End Time',
      'Type', 'Subtype', 'Distance', 'UUID',
      'From Lat', 'From Long', 'To Lat', 'To Long', 'Owner', 'Asset Id',
    ];
    const rows = filteredReports.map((r) => [
      formatDateTime(r.startTs, 'M/D/YY'),
      formatDateTime(r.startTs, 'h:mm a'),
      formatDateTime(r.endTs, 'M/D/YY'),
      formatDateTime(r.endTs, 'h:mm a'),
      r.assetType?.name || r.type || '',
      r.assetSubType?.name || r.subtype || '',
      r.distance ? `${Number(r.distance).toFixed(2)} mi` : '',
      r.uuid || r.assetUuid || '',
      r.fromLatitude || r.fromCoordinate?.latitude || '',
      r.fromLongitude || r.fromCoordinate?.longitude || '',
      r.toLatitude || r.toCoordinate?.latitude || '',
      r.toLongitude || r.toCoordinate?.longitude || '',
      r.owner || '',
      r.assetId || '',
    ].map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','));

    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `moves-report-${projectId || 'all'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <PageBody>
      {/* Title Row: INDIVIDUAL MOVES on left, Export Report on right */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="subtitle2" sx={{ fontSize: '1.125rem', fontWeight: 600 }}>
          INDIVIDUAL MOVES
        </Typography>
        <Button
          variant="outlined"
          startIcon={<ExportIcon />}
          onClick={handleExport}
          disabled={filteredReports.length === 0}
          aria-label="Export movement report"
          sx={{ height: 44 }}
        >
          Export Report
        </Button>
      </Box>

      {/* Helper text */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Display Movements From:
      </Typography>

      {/* Filter Row */}
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <DatePicker
            label="From"
            value={startDate}
            onChange={(date) => setStartDate(date)}
            slotProps={{ 
              textField: { 
                sx: { width: 160, marginBottom: 0 },
                size: 'small',
              }
            }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mx: 0.5 }}>—</Typography>
          <DatePicker
            label="To"
            value={endDate}
            onChange={(date) => setEndDate(date)}
            slotProps={{ 
              textField: { 
                sx: { width: 160, marginBottom: 0 },
                size: 'small',
              }
            }}
          />
          <FormControl size="small" sx={{ minWidth: 120, marginBottom: 0 }}>
            <InputLabel>Type</InputLabel>
            <Select value={typeFilter} label="Type" onChange={(e) => setTypeFilter(e.target.value)}>
              <MenuItem value="">ALL</MenuItem>
              {uniqueTypes.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120, marginBottom: 0 }}>
            <InputLabel>Subtype</InputLabel>
            <Select value={subtypeFilter} label="Subtype" onChange={(e) => setSubtypeFilter(e.target.value)}>
              <MenuItem value="">ALL</MenuItem>
              {uniqueSubtypes.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            startIcon={<RunIcon />}
            onClick={handleRunReport}
            sx={{ backgroundColor: colorPalette.asseTracOrange, height: 44 }}
          >
            Run Report
          </Button>
        </Box>
      </LocalizationProvider>

      {/* Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Paper sx={{
            backgroundColor: '#302F2E',
            borderRadius: '12px',
            border: '1px solid #4A4847',
            overflow: 'hidden',
            boxShadow: 'none',
            backgroundImage: 'none',
          }}>
          <TableContainer>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Start Date</TableCell>
                  <TableCell>Start Time</TableCell>
                  <TableCell>End Date</TableCell>
                  <TableCell>End Time</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Subtype</TableCell>
                  <TableCell>Distance</TableCell>
                  <TableCell>UUID</TableCell>
                  <TableCell>From Lat</TableCell>
                  <TableCell>From Long</TableCell>
                  <TableCell>To Lat</TableCell>
                  <TableCell>To Long</TableCell>
                  <TableCell>Owner</TableCell>
                  <TableCell>Asset Id</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedReports.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={14} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                        No movement reports found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedReports.map((r, idx) => (
                    <TableRow key={r.id || idx} hover>
                      <TableCell>{formatDateTime(r.startTs, 'M/D/YY')}</TableCell>
                      <TableCell>{formatDateTime(r.startTs, 'h:mm a')}</TableCell>
                      <TableCell>{formatDateTime(r.endTs, 'M/D/YY')}</TableCell>
                      <TableCell>{formatDateTime(r.endTs, 'h:mm a')}</TableCell>
                      <TableCell>{r.assetType?.name || r.type || '-'}</TableCell>
                      <TableCell>{r.assetSubType?.name || r.subtype || '-'}</TableCell>
                      <TableCell>{r.distance ? `${Number(r.distance).toFixed(2)} mi` : '-'}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                        {(r.uuid || r.assetUuid || '-').substring(0, 14)}...
                      </TableCell>
                      <TableCell>{(r.fromLatitude || r.fromCoordinate?.latitude)?.toFixed?.(6) ?? '-'}</TableCell>
                      <TableCell>{(r.fromLongitude || r.fromCoordinate?.longitude)?.toFixed?.(6) ?? '-'}</TableCell>
                      <TableCell>{(r.toLatitude || r.toCoordinate?.latitude)?.toFixed?.(6) ?? '-'}</TableCell>
                      <TableCell>{(r.toLongitude || r.toCoordinate?.longitude)?.toFixed?.(6) ?? '-'}</TableCell>
                      <TableCell>{r.owner || '-'}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                        {(r.assetId || '-').substring(0, 8)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredReports.length}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[8, 25, 50]}
          />
          </Paper>
        </>
      )}
    </PageBody>
  );
};

export default MovesTable;
